import { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Grid,
  Skeleton,
  Switch,
  FormControlLabel,
  Chip,
  Autocomplete,
  Stack,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';
import { useModule, useUpdateModule, useModules } from '../../../hooks/subscriptions';
import { useSnackbar } from '../../../components/SnackbarProvider';
import { ModuleCategory } from '../../../types/subscriptions';
import { getCategoryLabel } from '../../../utils/subscriptions';
import type { UpdateModuleDto, Module } from '../../../types/subscriptions';

export const ModuleEdit = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showMessage } = useSnackbar();

  const { data: module, isLoading } = useModule(id!);
  const { data: allModules } = useModules();
  const updateMutation = useUpdateModule(id!);

  const [dependencies, setDependencies] = useState<string[]>([]);

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<UpdateModuleDto>();

  // Populate form when module data loads
  useEffect(() => {
    if (module) {
      reset({
        name: module.name,
        key: module.key,
        description: module.description,
        category: module.category,
        isCore: module.isCore,
      });
      setDependencies(module.dependencies || []);
    }
  }, [module, reset]);

  // Get available modules for dependency selection (exclude self and already added)
  const availableModulesForDeps = (allModules || []).filter(
    (m) => m.key !== module?.key && !dependencies.includes(m.key)
  );

  const handleAddDependency = (moduleToAdd: Module | null) => {
    if (moduleToAdd && !dependencies.includes(moduleToAdd.key)) {
      setDependencies([...dependencies, moduleToAdd.key]);
    }
  };

  const handleRemoveDependency = (keyToRemove: string) => {
    setDependencies(dependencies.filter((key) => key !== keyToRemove));
  };

  const onSubmit = (data: UpdateModuleDto) => {
    // Include dependencies from state
    const payload = {
      ...data,
      dependencies,
    };
    updateMutation.mutate(payload, {
      onSuccess: () => {
        showMessage('Module updated successfully', 'success');
        navigate('/subscriptions/modules');
      },
      onError: (error: any) => {
        showMessage(error.response?.data?.message || 'Failed to update module', 'error');
      },
    });
  };

  if (isLoading) {
    return (
      <Box>
        <Skeleton variant="text" width={200} height={48} sx={{ mb: 3 }} />
        <Card>
          <CardContent>
            <Skeleton variant="rectangular" height={400} />
          </CardContent>
        </Card>
      </Box>
    );
  }

  if (!module) {
    return (
      <Box>
        <Typography variant="h4" mb={3}>
          Module Not Found
        </Typography>
        <Button variant="outlined" onClick={() => navigate('/subscriptions/modules')}>
          Back to Modules
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" mb={3}>
        Edit Module: {module.name}
      </Typography>

      <Card>
        <CardContent>
          <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
            <Grid container spacing={3}>
              {/* Basic Information */}
              <Grid item xs={12}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Basic Information
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  {...register('name', { required: 'Module name is required' })}
                  error={!!errors.name}
                  helperText={errors.name?.message}
                  fullWidth
                  label="Module Name"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  {...register('key', {
                    required: 'Key is required',
                    pattern: {
                      value: /^[a-z0-9_]+$/,
                      message: 'Key must be lowercase letters, numbers, and underscores only'
                    }
                  })}
                  error={!!errors.key}
                  helperText={errors.key?.message || 'Unique identifier'}
                  fullWidth
                  label="Module Key"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  {...register('description', { required: 'Description is required' })}
                  error={!!errors.description}
                  helperText={errors.description?.message}
                  fullWidth
                  label="Description"
                  multiline
                  rows={3}
                />
              </Grid>

              {/* Category */}
              <Grid item xs={12}>
                <Typography variant="h6" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                  Category
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth error={!!errors.category}>
                  <InputLabel>Category</InputLabel>
                  <Controller
                    name="category"
                    control={control}
                    rules={{ required: 'Category is required' }}
                    render={({ field }) => (
                      <Select {...field} label="Category">
                        {Object.values(ModuleCategory).map((category) => (
                          <MenuItem key={category} value={category}>
                            {getCategoryLabel(category)}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                  {errors.category && (
                    <FormHelperText>{errors.category.message}</FormHelperText>
                  )}
                </FormControl>
              </Grid>

              {/* Module Settings */}
              <Grid item xs={12}>
                <Typography variant="h6" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                  Module Settings
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Controller
                  name="isCore"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Switch
                          {...field}
                          checked={field.value || false}
                          onChange={(e) => field.onChange(e.target.checked)}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body1">Core Module</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Core modules are always available and cannot be disabled
                          </Typography>
                        </Box>
                      }
                    />
                  )}
                />
              </Grid>

              {/* Dependencies Editor */}
              <Grid item xs={12}>
                <Typography variant="h6" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                  Dependencies
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                  Select modules that must be enabled before this module can be used
                </Typography>

                {/* Current Dependencies */}
                {dependencies.length > 0 && (
                  <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2, gap: 1 }}>
                    {dependencies.map((depKey) => {
                      const depModule = allModules?.find((m) => m.key === depKey);
                      return (
                        <Chip
                          key={depKey}
                          label={depModule?.name || depKey}
                          onDelete={() => handleRemoveDependency(depKey)}
                          deleteIcon={<DeleteIcon />}
                          color="primary"
                          variant="outlined"
                        />
                      );
                    })}
                  </Stack>
                )}

                {/* Add Dependency */}
                <Autocomplete
                  options={availableModulesForDeps}
                  getOptionLabel={(option) => `${option.name} (${option.key})`}
                  onChange={(_, value) => handleAddDependency(value)}
                  value={null}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Add Dependency"
                      placeholder="Search modules..."
                      size="small"
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      <Box>
                        <Typography variant="body2">{option.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.key} - {getCategoryLabel(option.category)}
                        </Typography>
                      </Box>
                    </li>
                  )}
                  sx={{ maxWidth: 400 }}
                />

                {dependencies.length === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    No dependencies configured
                  </Typography>
                )}
              </Grid>
            </Grid>

            <Box display="flex" gap={2} mt={4}>
              <Button
                type="submit"
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={updateMutation.isPending}
              >
                {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
              </Button>
              <Button
                variant="outlined"
                startIcon={<CancelIcon />}
                onClick={() => navigate('/subscriptions/modules')}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};
